/**
 * Query Key Factory
 *
 * Provides consistent query key generation for React Query.
 * This ensures proper cache invalidation and avoids key collisions.
 *
 * @see https://tanstack.com/query/latest/docs/react/guides/query-keys
 *
 * Key Structure:
 * - All keys start with the resource type for broad invalidation
 * - 'list' keys include filters for granular caching
 * - 'detail' keys include the resource ID
 * - Nested resources include parent IDs
 */

import { QueryKeyPrefix, ResourceType } from '@access-cms/shared-constants';

import type { QueryParamRecord } from './types';

// =============================================================================
// Query Key Types
// =============================================================================

/**
 * Filter record type for query keys - uses the same type as query params
 */
type FilterRecord = QueryParamRecord;

/**
 * Base query key tuple type - query keys can contain strings, numbers, or filter objects
 */
export type QueryKeyBase = readonly (string | number | FilterRecord)[];

/**
 * Query key for list queries
 */
export type ListQueryKey<T extends ResourceType = ResourceType> = readonly [T, 'list', FilterRecord?];

/**
 * Query key for detail queries
 */
export type DetailQueryKey<T extends ResourceType = ResourceType> = readonly [T, 'detail', string, FilterRecord?];

/**
 * Query key for nested resource lists
 */
export type NestedListQueryKey<T extends ResourceType = ResourceType> = readonly [
  ResourceType,
  'nested',
  string,
  T,
  'list',
  FilterRecord?,
];

/**
 * Query key for nested resource details
 */
export type NestedDetailQueryKey<T extends ResourceType = ResourceType> = readonly [
  ResourceType,
  'nested',
  string,
  T,
  'detail',
  string,
];

// =============================================================================
// Query Key Factory
// =============================================================================

/**
 * Factory for creating consistent query keys
 *
 * @example
 * ```ts
 * // List all users
 * queryClient.invalidateQueries({ queryKey: queryKeys.users.list() });
 *
 * // Get specific user
 * queryClient.invalidateQueries({ queryKey: queryKeys.users.detail('123') });
 *
 * // Invalidate all user queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
 *
 * // List roster for a contract
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.contracts.nested('contract-id').roster.list()
 * });
 * ```
 */
export const queryKeys = {
  // =========================================================================
  // Applications
  // =========================================================================
  [ResourceType.APPLICATIONS]: {
    all: [ResourceType.APPLICATIONS] as const,
    detail: (id: string, includes?: FilterRecord) =>
      includes
        ? ([ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL, id, includes] as const)
        : ([ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL, id] as const),
    details: () => [ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL] as const,
    foreignNationalInfo: (id: string) =>
      [ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL, id, 'foreign-national-information'] as const,
    history: (id: string) => [ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL, id, 'history'] as const,
    list: (filters?: FilterRecord) => [ResourceType.APPLICATIONS, QueryKeyPrefix.LIST, filters ?? {}] as const,
    lists: () => [ResourceType.APPLICATIONS, QueryKeyPrefix.LIST] as const,
    me: () => [ResourceType.APPLICATIONS, 'me'] as const,
    timeline: (id: string) => [ResourceType.APPLICATIONS, QueryKeyPrefix.DETAIL, id, 'timeline'] as const,
  },

  // =========================================================================
  // Auth
  // =========================================================================
  [QueryKeyPrefix.AUTH]: {
    me: [QueryKeyPrefix.AUTH, 'me'] as const,
  },

  // =========================================================================
  // Approvals
  // =========================================================================
  [ResourceType.APPROVALS]: {
    all: [ResourceType.APPROVALS] as const,
    detail: (id: string, includes?: FilterRecord) =>
      includes
        ? ([ResourceType.APPROVALS, QueryKeyPrefix.DETAIL, id, includes] as const)
        : ([ResourceType.APPROVALS, QueryKeyPrefix.DETAIL, id] as const),
    details: () => [ResourceType.APPROVALS, QueryKeyPrefix.DETAIL] as const,
    list: (filters?: FilterRecord) => [ResourceType.APPROVALS, QueryKeyPrefix.LIST, filters ?? {}] as const,
    lists: () => [ResourceType.APPROVALS, QueryKeyPrefix.LIST] as const,
  },

  // =========================================================================
  // Contracts
  // =========================================================================
  [ResourceType.CONTRACTS]: {
    all: [ResourceType.CONTRACTS] as const,
    detail: (id: string, includes?: FilterRecord) =>
      includes
        ? ([ResourceType.CONTRACTS, QueryKeyPrefix.DETAIL, id, includes] as const)
        : ([ResourceType.CONTRACTS, QueryKeyPrefix.DETAIL, id] as const),
    details: () => [ResourceType.CONTRACTS, QueryKeyPrefix.DETAIL] as const,
    list: (filters?: FilterRecord) => [ResourceType.CONTRACTS, QueryKeyPrefix.LIST, filters ?? {}] as const,
    lists: () => [ResourceType.CONTRACTS, QueryKeyPrefix.LIST] as const,
    // Nested resources under contracts
    nested: (contractId: string) => ({
      // CORs
      cors: {
        all: [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, 'cors'] as const,
        detail: (userId: string) =>
          [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, 'cors', QueryKeyPrefix.DETAIL, userId] as const,
        list: () => [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, 'cors', QueryKeyPrefix.LIST] as const,
      },

      // Labor Categories
      laborCategories: {
        all: [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, ResourceType.LABOR_CATEGORIES] as const,
        detail: (id: string) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.LABOR_CATEGORIES,
            QueryKeyPrefix.DETAIL,
            id,
          ] as const,
        list: (filters?: FilterRecord) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.LABOR_CATEGORIES,
            QueryKeyPrefix.LIST,
            filters ?? {},
          ] as const,
      },

      // Role Assignments
      roleAssignments: {
        all: [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, ResourceType.ROLE_ASSIGNMENTS] as const,
        detail: (assignmentId: string) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.ROLE_ASSIGNMENTS,
            QueryKeyPrefix.DETAIL,
            assignmentId,
          ] as const,
        list: (filters?: FilterRecord) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.ROLE_ASSIGNMENTS,
            QueryKeyPrefix.LIST,
            filters ?? {},
          ] as const,
      },

      // Roster
      roster: {
        all: [ResourceType.CONTRACTS, QueryKeyPrefix.NESTED, contractId, ResourceType.ROSTER] as const,
        detail: (userId: string) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.ROSTER,
            QueryKeyPrefix.DETAIL,
            userId,
          ] as const,
        list: (filters?: FilterRecord) =>
          [
            ResourceType.CONTRACTS,
            QueryKeyPrefix.NESTED,
            contractId,
            ResourceType.ROSTER,
            QueryKeyPrefix.LIST,
            filters ?? {},
          ] as const,
      },
    }),

    search: (filters?: FilterRecord) => [ResourceType.CONTRACTS, 'search', filters ?? {}] as const,
  },

  // =========================================================================
  // Out-of-Office
  // =========================================================================
  [ResourceType.OUT_OF_OFFICE]: {
    all: [ResourceType.OUT_OF_OFFICE] as const,
    detail: (id: string) => [ResourceType.OUT_OF_OFFICE, QueryKeyPrefix.DETAIL, id] as const,
    details: () => [ResourceType.OUT_OF_OFFICE, QueryKeyPrefix.DETAIL] as const,
    list: () => [ResourceType.OUT_OF_OFFICE, QueryKeyPrefix.LIST] as const,
    lists: () => [ResourceType.OUT_OF_OFFICE, QueryKeyPrefix.LIST] as const,
  },

  // =========================================================================
  // Users
  // =========================================================================
  [ResourceType.USERS]: {
    all: [ResourceType.USERS] as const,
    detail: (id: string, includes?: FilterRecord) =>
      includes
        ? ([ResourceType.USERS, QueryKeyPrefix.DETAIL, id, includes] as const)
        : ([ResourceType.USERS, QueryKeyPrefix.DETAIL, id] as const),
    details: () => [ResourceType.USERS, QueryKeyPrefix.DETAIL] as const,
    list: (filters?: FilterRecord) => [ResourceType.USERS, QueryKeyPrefix.LIST, filters ?? {}] as const,
    lists: () => [ResourceType.USERS, QueryKeyPrefix.LIST] as const,
  },
} as const;

// =============================================================================
// Dynamic Query Key Generation
// =============================================================================

/**
 * Generate a query key for a given path and parameters
 * Useful for generic hooks that work with any path
 */
export function createQueryKey(
  resource: ResourceType,
  type: 'list' | 'detail',
  idOrFilters?: string | FilterRecord,
): QueryKeyBase {
  if (type === QueryKeyPrefix.LIST) {
    return [resource, QueryKeyPrefix.LIST, (idOrFilters as FilterRecord) ?? {}] as const;
  }

  return [resource, QueryKeyPrefix.DETAIL, idOrFilters as string] as const;
}

/**
 * Generate a query key for nested resources
 */
export function createNestedQueryKey(
  parentResource: ResourceType,
  parentId: string,
  childResource: ResourceType,
  type: 'list' | 'detail',
  idOrFilters?: string | FilterRecord,
): QueryKeyBase {
  if (type === QueryKeyPrefix.LIST) {
    return [
      parentResource,
      QueryKeyPrefix.NESTED,
      parentId,
      childResource,
      QueryKeyPrefix.LIST,
      (idOrFilters as FilterRecord) ?? {},
    ] as const;
  }

  return [
    parentResource,
    QueryKeyPrefix.NESTED,
    parentId,
    childResource,
    QueryKeyPrefix.DETAIL,
    idOrFilters as string,
  ] as const;
}

// =============================================================================
// Invalidation Helpers
// =============================================================================

/**
 * Invalidation key type - array of query key tuples for invalidation
 * Uses QueryKeyBase to allow filter objects in keys
 */
type InvalidationKeys = readonly QueryKeyBase[];

/**
 * Get query keys to invalidate when a resource is mutated
 * This provides smart invalidation based on relationships
 */
export function getInvalidationKeys(resource: ResourceType, parentId?: string): InvalidationKeys {
  switch (resource) {
    case ResourceType.USERS:
      return [queryKeys[ResourceType.USERS].all];

    case ResourceType.CONTRACTS:
      return [queryKeys[ResourceType.CONTRACTS].all];

    case ResourceType.APPLICATIONS:
      return [queryKeys[ResourceType.APPLICATIONS].all];

    case ResourceType.APPROVALS:
      return [queryKeys[ResourceType.APPROVALS].all];

    case ResourceType.OUT_OF_OFFICE:
      return [queryKeys[ResourceType.OUT_OF_OFFICE].all];

    case ResourceType.ROSTER:
      if (parentId) {
        return [
          queryKeys[ResourceType.CONTRACTS].nested(parentId).roster.all,
          queryKeys[ResourceType.CONTRACTS].detail(parentId),
        ];
      }

      return [queryKeys[ResourceType.CONTRACTS].all];

    case ResourceType.LABOR_CATEGORIES:
      if (parentId) {
        return [
          queryKeys[ResourceType.CONTRACTS].nested(parentId).laborCategories.all,
          queryKeys[ResourceType.CONTRACTS].nested(parentId).roster.all,
        ];
      }

      return [queryKeys[ResourceType.CONTRACTS].all];

    case ResourceType.ROLE_ASSIGNMENTS:
      if (parentId) {
        return [
          queryKeys[ResourceType.CONTRACTS].nested(parentId).roleAssignments.all,
          queryKeys[ResourceType.CONTRACTS].detail(parentId),
        ];
      }

      return [queryKeys[ResourceType.CONTRACTS].all];

    default:
      return [];
  }
}
